import { type InfraCostsSummary } from '@carsfinders/contracts';

import { t } from '../../../lib/i18n';
import { cn } from '../../clients/utils';
import { approx, formatUsd } from '../format';

type KpiKey = 'thisMonth' | 'next30' | 'overdue' | 'next12' | 'paidThisMonth' | 'paid12';

interface Tile {
  key: KpiKey;
  value: string;
  tone: 'default' | 'danger' | 'paid';
}

/**
 * The six figures, all USD at the rule set's rates. The four schedule figures
 * carry «≈» when an estimate is inside; the two paid figures are recorded
 * payments — facts — so they never do, and wear the success rule instead.
 */
export function buildKpiTiles(summary: InfraCostsSummary): Tile[] {
  const money = (usd: number) => approx(formatUsd(usd), summary.hasEstimates);
  return [
    { key: 'thisMonth', value: money(summary.thisMonthUsd), tone: 'default' },
    { key: 'next30', value: money(summary.next30DaysUsd), tone: 'default' },
    {
      key: 'overdue',
      value:
        summary.overdue.count > 0
          ? t('admin.infraCosts.kpi.overdueValue', {
              count: String(summary.overdue.count),
              usd: money(summary.overdue.usd),
            })
          : t('admin.infraCosts.kpi.overdueNone'),
      tone: summary.overdue.count > 0 ? 'danger' : 'default',
    },
    { key: 'next12', value: money(summary.next12MonthsUsd), tone: 'default' },
    { key: 'paidThisMonth', value: formatUsd(summary.paidThisMonthUsd), tone: 'paid' },
    { key: 'paid12', value: formatUsd(summary.paidLast12MonthsUsd), tone: 'paid' },
  ];
}

export function InfraKpiTiles({ summary }: { summary: InfraCostsSummary }) {
  const tiles = buildKpiTiles(summary);
  return (
    <section aria-label={t('admin.infraCosts.kpi.label')} className="flex flex-col gap-1.5">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-3" data-testid="infra-kpi">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            data-kpi={tile.key}
            className={cn(
              'rounded-cf-lg border bg-cf-surface px-3.5 py-3 lg:px-[18px] lg:py-4',
              tile.tone === 'danger' && 'border-cf-danger shadow-[inset_0_3px_0_var(--color-cf-danger)]',
              tile.tone === 'default' && 'border-cf-line shadow-[inset_0_3px_0_var(--color-cf-accent)]',
              tile.tone === 'paid' && 'border-cf-line shadow-[inset_0_3px_0_var(--color-cf-success)]',
            )}
          >
            <h2 className="font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:text-[11px]">
              {t(`admin.infraCosts.kpi.${tile.key}`)}
            </h2>
            <p
              data-value
              className={cn(
                'mt-2 font-mono text-[20px] leading-none font-medium lg:text-[24px]',
                tile.tone === 'danger' ? 'text-cf-danger' : 'text-cf-ink',
              )}
            >
              {tile.value}
            </p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-cf-ink-subtle" data-testid="infra-kpi-fx">
        {t('admin.infraCosts.kpi.fxNote', { version: summary.fx.asOf || '—' })}
        {summary.hasEstimates ? ` · ${t('admin.infraCosts.kpi.estimateNote')}` : ''}
      </p>
    </section>
  );
}

/** Six tile frames while the summary loads — nothing that pretends to be a number. */
export function InfraKpiTilesSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-3" aria-busy="true">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-[76px] animate-pulse rounded-cf-lg border border-cf-line bg-cf-surface" />
      ))}
    </div>
  );
}
